import { API_BASE_URL as API_BASE } from '../config/api';
import { createAuthenticatedApiClient } from './authenticatedApiClient';
import {
  createApiResponse,
  createUserWatchlist,
  createWatchlistItem,
  createWatchlistStatistics,
  createWatchlistItemWithCompany,
  createAddToWatchlistRequest,
  createUpdateWatchlistItemRequest,
  createAddUserAddressRequest,
  createAddRouteRequest,
  createUpdateRouteStatusRequest,
  createUserAddress,
  createRoute
} from '../models/watchlistModels';
import { createRouteAnalysis } from '../models/routeAnalysisModels';
import { getStoredUserId } from './authSession';
import {
  clearEtagByPrefix,
  clearEtagEntry,
  etagKeyForUser,
  getEtagEntry,
  setEtagEntry,
} from './etagStore';
import {
  mapReadModelStatistics,
  mapReadModelToWatchlistItems,
} from '../utils/watchlistReadModelAdapter';

// Base: /api/v1/watchlist. Addresses: GET/POST/DELETE /{userId}/{companyId}/addresses.
// Routes: GET/POST/DELETE /{userId}/{companyId}/routes, GET .../routes/{routeId}/analysis, PUT .../routes/{routeId}/status.
// See Watchlist Addresses & Routes API doc for request/response (camelCase request body, snake_case in response data).
const API_BASE_URL = `${API_BASE}/api/v1/watchlist`;

const apiClient = createAuthenticatedApiClient({ baseURL: API_BASE_URL });

const routeIdOf = (route) => route?.routeId ?? route?.route_id;
const routeOwnerCompanyIdOf = (route, fallbackCompanyId) =>
  route?._ownerCompanyId ?? route?.companyId ?? route?.company_id ?? fallbackCompanyId;

const WATCHLIST_ETAG_RESOURCE = 'watchlist-read-model';
const ROUTE_BATCH_SIZE = 50;

const invalidateWatchlistCache = (userId) => {
  if (userId) {
    clearEtagEntry(etagKeyForUser(WATCHLIST_ETAG_RESOURCE, userId));
  } else {
    clearEtagByPrefix(`${WATCHLIST_ETAG_RESOURCE}:`);
  }
};

const chunkArray = (items, size) => {
  const chunks = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
};

// Helper function to get userId from localStorage
const getUserId = () => {
  return getStoredUserId();
};

export const watchlistService = {
  // Watchlist Management
  async getUserWatchlist(userId) {
    try {
      const response = await apiClient.get(`/${userId}`);
      const apiResponse = createApiResponse(response.data);
      if (apiResponse.success && apiResponse.data) {
        apiResponse.data = createUserWatchlist(apiResponse.data);
      }
      return apiResponse;
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch watchlist'
      });
    }
  },

  async addToWatchlist(userId, requestData) {
    try {
      const request = createAddToWatchlistRequest(requestData);
      const validationErrors = request.validate();
      if (validationErrors.length > 0) {
        return createApiResponse({
          success: false,
          message: `Validation errors: ${validationErrors.join(', ')}`
        });
      }
      const response = await apiClient.post(`/${userId}/add`, request.toJSON());
      const apiResponse = createApiResponse(response.data);
      if (apiResponse.success && apiResponse.data) {
        apiResponse.data = createWatchlistItem(apiResponse.data);
        invalidateWatchlistCache(userId);
      }
      return apiResponse;
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to add to watchlist'
      });
    }
  },

  async removeFromWatchlist(userId, companyId) {
    try {
      const response = await apiClient.delete(`/${userId}/companies/${companyId}`);
      const apiResponse = createApiResponse(response.data);
      if (apiResponse.success) invalidateWatchlistCache(userId);
      return apiResponse;
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to remove from watchlist'
      });
    }
  },

  async updateWatchlistItem(userId, companyId, requestData) {
    try {
      const request = createUpdateWatchlistItemRequest(requestData);
      const validationErrors = request.validate();
      if (validationErrors.length > 0) {
        return createApiResponse({
          success: false,
          message: `Validation errors: ${validationErrors.join(', ')}`
        });
      }

      const response = await apiClient.put(`/${userId}/${companyId}/update`, request.toJSON());
      const apiResponse = createApiResponse(response.data);
      if (apiResponse.success && apiResponse.data) {
        apiResponse.data = createWatchlistItem(apiResponse.data);
        invalidateWatchlistCache(userId);
      }
      return apiResponse;
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to update watchlist item'
      });
    }
  },

  // Company Type Management
  async setCompanyType(userId, companyId, companyType) {
    try {
      const response = await apiClient.put(`/${userId}/${companyId}/company-type?companyType=${companyType}`);
      const apiResponse = createApiResponse(response.data);
      if (apiResponse.success) invalidateWatchlistCache(userId);
      return apiResponse;
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to set company type'
      });
    }
  },

  // Address Management
  async addUserAddress(userId, companyId, requestData) {
    try {
      const request = createAddUserAddressRequest(requestData);
      const validationErrors = request.validate();
      if (validationErrors.length > 0) {
        return createApiResponse({
          success: false,
          message: `Validation errors: ${validationErrors.join(', ')}`
        });
      }

      const response = await apiClient.post(`/${userId}/${companyId}/addresses`, request.toJSON());
      const apiResponse = createApiResponse(response.data);
      if (apiResponse.success && apiResponse.data) {
        apiResponse.data = createUserAddress(apiResponse.data);
        invalidateWatchlistCache(userId);
      }
      return apiResponse;
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to add address'
      });
    }
  },

  async getUserAddresses(userId, companyId) {
    try {
      const response = await apiClient.get(`/${userId}/${companyId}/addresses`);
      const apiResponse = createApiResponse(response.data);
      if (apiResponse.success && Array.isArray(apiResponse.data)) {
        apiResponse.data = apiResponse.data.map((addr) => createUserAddress(addr));
      } else if (apiResponse.success && apiResponse.data == null) {
        apiResponse.data = [];
      }
      return apiResponse;
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch addresses'
      });
    }
  },

  async removeUserAddress(userId, companyId, addressId) {
    try {
      const response = await apiClient.delete(`/${userId}/${companyId}/addresses/${addressId}`);
      const apiResponse = createApiResponse(response.data);
      if (apiResponse.success) invalidateWatchlistCache(userId);
      return apiResponse;
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to remove address'
      });
    }
  },

  // Route Management
  async addRoute(userId, companyId, requestData) {
    try {
      const request = createAddRouteRequest(requestData);
      const validationErrors = request.validate();
      if (validationErrors.length > 0) {
        return createApiResponse({
          success: false,
          message: `Validation errors: ${validationErrors.join(', ')}`
        });
      }

      const response = await apiClient.post(`/${userId}/${companyId}/routes`, request.toJSON());
      const apiResponse = createApiResponse(response.data);
      if (apiResponse.success && apiResponse.data) {
        apiResponse.data = createRoute(apiResponse.data);
        invalidateWatchlistCache(userId);
      }
      return apiResponse;
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to add route'
      });
    }
  },

  async getRoutes(userId, companyId) {
    try {
      const response = await apiClient.get(`/${userId}/${companyId}/routes`);
      const apiResponse = createApiResponse(response.data);
      if (apiResponse.success && Array.isArray(apiResponse.data)) {
        apiResponse.data = apiResponse.data.map((route) => createRoute(route));
      } else if (apiResponse.success && apiResponse.data == null) {
        apiResponse.data = [];
      }
      return apiResponse;
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch routes'
      });
    }
  },

  async getRoutesByAddress(userId, companyId, addressId) {
    try {
      const response = await apiClient.get(`/${userId}/${companyId}/routes/by-address/${addressId}`);
      const apiResponse = createApiResponse(response.data);
      if (apiResponse.success && apiResponse.data && Array.isArray(apiResponse.data)) {
        apiResponse.data = apiResponse.data.map(route => createRoute(route));
      }
      return apiResponse;
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch routes by address'
      });
    }
  },

  async updateRoute(userId, companyId, routeId, requestData) {
    try {
      const response = await apiClient.put(`/${userId}/${companyId}/routes/${routeId}`, requestData);
      const apiResponse = createApiResponse(response.data);
      if (apiResponse.success && apiResponse.data) {
        apiResponse.data = createRoute(apiResponse.data);
        invalidateWatchlistCache(userId);
      }
      return apiResponse;
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to update route'
      });
    }
  },

  async updateRouteStatus(userId, companyId, routeId, isActive) {
    try {
      const requestData = createUpdateRouteStatusRequest({ isActive });
      const response = await apiClient.put(`/${userId}/${companyId}/routes/${routeId}/status`, requestData.toJSON());
      const apiResponse = createApiResponse(response.data);
      if (apiResponse.success && apiResponse.data) {
        apiResponse.data = createRoute(apiResponse.data);
        invalidateWatchlistCache(userId);
      }
      return apiResponse;
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to update route status'
      });
    }
  },

  async removeRoute(userId, companyId, routeId) {
    try {
      const response = await apiClient.delete(`/${userId}/${companyId}/routes/${routeId}`);
      const apiResponse = createApiResponse(response.data);
      if (apiResponse.success) invalidateWatchlistCache(userId);
      return apiResponse;
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to remove route'
      });
    }
  },

  // Route Analysis
  async getRouteAnalysis(userId, companyId, routeId) {
    try {
      const response = await apiClient.get(`/${userId}/${companyId}/routes/${routeId}/analysis`);
      const apiResponse = createApiResponse(response.data);
      if (apiResponse.success && apiResponse.data) {
        apiResponse.data = createRouteAnalysis(apiResponse.data);
      }
      return apiResponse;
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch route analysis'
      });
    }
  },

  async getAllRoutesAnalysis(userId, companyId) {
    try {
      const response = await apiClient.get(`/${userId}/${companyId}/routes/analysis`);
      const apiResponse = createApiResponse(response.data);
      if (apiResponse.success && apiResponse.data && Array.isArray(apiResponse.data)) {
        apiResponse.data = apiResponse.data.map((analysis) => createRouteAnalysis(analysis));
      }
      return apiResponse;
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch routes analysis'
      });
    }
  },

  async getRouteAnalysesBatch(userId, routes = [], options = {}) {
    try {
      if (!Array.isArray(routes) || routes.length === 0) {
        return createApiResponse({ success: true, data: [] });
      }

      const { forceRefresh = false, maxAgeMinutes = 1440 } = options;
      const refs = routes
        .map((route) => ({
          company_id: routeOwnerCompanyIdOf(route),
          route_id: routeIdOf(route),
        }))
        .filter((ref) => ref.company_id && ref.route_id);

      if (refs.length === 0) {
        return createApiResponse({ success: true, data: [] });
      }

      const analyses = [];
      const chunks = chunkArray(refs, ROUTE_BATCH_SIZE);

      for (const chunk of chunks) {
        try {
          const response = await apiClient.post(`/${userId}/routes/analysis/batch`, {
            routes: chunk,
            force_refresh: forceRefresh,
            max_age_minutes: maxAgeMinutes,
          });
          const apiResponse = createApiResponse(response.data);
          const rawAnalyses = apiResponse.data?.analyses ?? apiResponse.data ?? {};
          Object.values(rawAnalyses).forEach((analysis) => {
            if (analysis) analyses.push(createRouteAnalysis(analysis));
          });
        } catch (batchError) {
          if (batchError.response?.status === 404) {
            const fallbackResults = await Promise.all(
              chunk.map(async ({ company_id: companyId, route_id: routeId }) => {
                const singleRes = await this.getRouteAnalysis(userId, companyId, routeId);
                return singleRes.success ? singleRes.data : null;
              })
            );
            analyses.push(...fallbackResults.filter(Boolean));
          } else {
            throw batchError;
          }
        }
      }

      return createApiResponse({
        success: true,
        data: analyses,
      });
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch batch route analyses',
      });
    }
  },

  async getWatchlistReadModel(userId, options = {}) {
    const etagKey = etagKeyForUser(WATCHLIST_ETAG_RESOURCE, userId);
    const stored = options.etag != null ? { etag: options.etag } : getEtagEntry(etagKey);

    try {
      const params = {};
      if (options.includeInboundRoutes === false) params.includeInboundRoutes = false;
      if (options.updatedSince) params.updatedSince = options.updatedSince;
      if (options.view) params.view = options.view;

      const headers = {};
      if (stored?.etag) headers['If-None-Match'] = stored.etag;

      const response = await apiClient.get(`/${userId}/read-model`, {
        params,
        headers,
        validateStatus: (status) => (status >= 200 && status < 300) || status === 304,
      });

      const responseEtag = response.headers?.etag ?? stored?.etag ?? '';

      if (response.status === 304) {
        return createApiResponse({
          success: true,
          notModified: true,
          etag: responseEtag,
          syncToken: stored?.syncToken ?? options.syncToken ?? '',
        });
      }

      const apiResponse = createApiResponse(response.data);
      if (!apiResponse.success || !apiResponse.data) {
        return apiResponse;
      }

      const payload = apiResponse.data;
      const items = mapReadModelToWatchlistItems(payload.items ?? []);
      const statistics = mapReadModelStatistics(payload.statistics);
      const syncToken = payload.sync_token ?? payload.syncToken ?? '';

      setEtagEntry(etagKey, { etag: responseEtag, syncToken });

      return createApiResponse({
        success: true,
        data: {
          userId: payload.user_id ?? userId,
          items,
          statistics,
          lastModified: payload.last_modified ?? payload.lastModified ?? null,
          syncToken,
        },
        etag: responseEtag,
        syncToken,
      });
    } catch (error) {
      if (error.response?.status === 404) {
        return this.getWatchlistReadModelLegacy(userId, options);
      }
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch watchlist read model',
      });
    }
  },

  /**
   * Load watchlist items with addresses and routes for map rendering.
   * Summary read-model responses may omit nested address data; legacy enrichment fills gaps.
   */
  async fetchWatchlistForMap(userId, cachedItems = []) {
    if (!userId) return [];

    const readModelRes = await this.getWatchlistReadModel(userId, {
      includeInboundRoutes: true,
    });

    let items = readModelRes.notModified
      ? cachedItems
      : readModelRes.success && Array.isArray(readModelRes.data?.items)
        ? readModelRes.data.items
        : [];

    const addressCount = items.reduce(
      (sum, item) => sum + (item.userCompanyInfo?.userAddresses?.length ?? 0),
      0
    );

    if (items.length > 0 && addressCount === 0) {
      const legacyRes = await this.getWatchlistReadModelLegacy(userId, {});
      if (legacyRes.success && Array.isArray(legacyRes.data?.items)) {
        items = legacyRes.data.items;
      }
    } else if (items.length === 0) {
      const legacyRes = await this.getWatchlistReadModelLegacy(userId, {});
      if (legacyRes.success && Array.isArray(legacyRes.data?.items)) {
        items = legacyRes.data.items;
      }
    }

    return items;
  },

  async getWatchlistReadModelLegacy(userId, options = {}) {
    try {
      const [itemsRes, statsRes] = await Promise.all([
        this.getWatchlistGraph(userId, {
          include: 'companySummary,addresses,routes,inboundRoutes',
          view: options.view ?? 'summary',
          updatedSince: options.updatedSince,
        }),
        this.getWatchlistStatistics(userId),
      ]);

      if (!itemsRes.success) {
        return itemsRes;
      }

      const rawItems = Array.isArray(itemsRes.data) ? itemsRes.data : [];
      const enriched = await Promise.all(
        rawItems.map(async (item) => {
          const cid = item.companyId ?? item.company_id ?? '';
          let userAddresses = item.userCompanyInfo?.userAddresses ?? [];
          let routes = item.userCompanyInfo?.routes ?? [];

          if (
            (!userAddresses.length || !routes.length) &&
            cid
          ) {
            const [addrRes, routesRes] = await Promise.all([
              this.getUserAddresses(userId, cid),
              this.getRoutes(userId, cid),
            ]);
            if (addrRes.success && Array.isArray(addrRes.data)) userAddresses = addrRes.data;
            if (routesRes.success && Array.isArray(routesRes.data)) routes = routesRes.data;
          }

          const existingInfo = item.userCompanyInfo;
          const existingRaw =
            existingInfo && typeof existingInfo === 'object'
              ? existingInfo.toJSON
                ? existingInfo.toJSON()
                : { ...existingInfo }
              : {};
          const companyType = String(existingRaw.company_type ?? existingInfo?.companyType ?? '')
            .trim()
            .toLowerCase();

          return {
            ...item,
            companyId: cid,
            userCompanyInfo: {
              ...existingRaw,
              companyType,
              company_type: companyType,
              userAddresses,
              routes,
            },
          };
        })
      );

      return createApiResponse({
        success: true,
        data: {
          userId,
          items: mapReadModelToWatchlistItems(
            enriched.map((item) => ({
              company_id: item.companyId,
              added_date: item.addedDate,
              notes: item.notes,
              alert_enabled: item.alertEnabled,
              priority: item.priority,
              port_start_point: item.portStartPoint,
              port_end_point: item.portEndPoint,
              company_type: item.userCompanyInfo?.companyType,
              company_summary: item.company
                ? {
                    ticker: item.company.ticker,
                    name: item.company.name,
                    industry: item.company.industry,
                    overall_risk_score: item.company.overallRiskScore,
                  }
                : null,
              addresses: item.userCompanyInfo?.userAddresses ?? [],
              routes: item.userCompanyInfo?.routes ?? [],
              route_analyses: item.routeAnalyses ?? {},
              inbound_routes: [],
            }))
          ),
          statistics: statsRes.success ? statsRes.data : null,
          syncToken: '',
        },
        legacy: true,
      });
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch watchlist',
      });
    }
  },

  // Query Methods
  async getWatchlistItems(userId) {
    try {
      const response = await apiClient.get(`/${userId}/items`);
      const apiResponse = createApiResponse(response.data);
      if (apiResponse.success && apiResponse.data && Array.isArray(apiResponse.data)) {
        apiResponse.data = apiResponse.data.map((item) => createWatchlistItem(item));
      }
      return apiResponse;
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch watchlist items'
      });
    }
  },

  async getWatchlistItemsByPriority(userId, priority) {
    try {
      const response = await apiClient.get(`/${userId}/items/priority/${priority}`);
      const apiResponse = createApiResponse(response.data);
      if (apiResponse.success && apiResponse.data && Array.isArray(apiResponse.data)) {
        apiResponse.data = apiResponse.data.map(item => createWatchlistItem(item));
      }
      return apiResponse;
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch watchlist items by priority'
      });
    }
  },

  async getWatchlistItemsWithAlerts(userId) {
    try {
      const response = await apiClient.get(`/${userId}/items/alerts`);
      const apiResponse = createApiResponse(response.data);
      if (apiResponse.success && apiResponse.data && Array.isArray(apiResponse.data)) {
        apiResponse.data = apiResponse.data.map(item => createWatchlistItem(item));
      }
      return apiResponse;
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch watchlist items with alerts'
      });
    }
  },

  async getWatchlistWithCompanies(userId, options = {}) {
    try {
      const params = {};
      if (options.include) params.include = options.include;
      if (options.view) params.view = options.view;
      if (options.fields) params.fields = options.fields;
      if (options.updatedSince) params.updatedSince = options.updatedSince;
      const response = await apiClient.get(`/${userId}/with-companies`, { params });
      const apiResponse = createApiResponse(response.data);
      if (apiResponse.success && apiResponse.data && Array.isArray(apiResponse.data)) {
        apiResponse.data = apiResponse.data.map(item => createWatchlistItemWithCompany(item));
      }
      return apiResponse;
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch watchlist with companies'
      });
    }
  },

  async getWatchlistGraph(userId, options = {}) {
    try {
      const params = {};
      if (options.include) params.include = options.include;
      if (options.view) params.view = options.view;
      if (options.fields) params.fields = options.fields;
      if (options.updatedSince) params.updatedSince = options.updatedSince;
      const response = await apiClient.get(`/${userId}/graph`, { params });
      return createApiResponse(response.data);
    } catch (_) {
      return this.getWatchlistWithCompanies(userId, options);
    }
  },

  // Statistics
  async getWatchlistStatistics(userId) {
    try {
      const response = await apiClient.get(`/${userId}/statistics`);
      const apiResponse = createApiResponse(response.data);
      if (apiResponse.success && apiResponse.data) {
        apiResponse.data = createWatchlistStatistics(apiResponse.data);
      }
      return apiResponse;
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch watchlist statistics'
      });
    }
  },

  // Additional Methods
  async updateWatchlistMetadata(userId, metadata) {
    try {
      const response = await apiClient.put(`/${userId}/metadata`, metadata);
      const apiResponse = createApiResponse(response.data);
      if (apiResponse.success && apiResponse.data) {
        apiResponse.data = createUserWatchlist(apiResponse.data);
      }
      return apiResponse;
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to update watchlist metadata'
      });
    }
  },

  async migrateUserWatchlist(userId) {
    try {
      const response = await apiClient.post(`/${userId}/migrate`);
      return createApiResponse(response.data);
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to migrate watchlist data'
      });
    }
  },

  async getDebugInfo(userId) {
    try {
      const response = await apiClient.get(`/${userId}/debug`);
      return createApiResponse(response.data);
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch debug information'
      });
    }
  },

  // Convenience methods that use localStorage userId
  async getCurrentUserWatchlist() {
    const userId = getUserId();
    if (!userId) {
      return createApiResponse({
        success: false,
        message: 'User not authenticated'
      });
    }
    return this.getUserWatchlist(userId);
  },

  async addToCurrentUserWatchlist(requestData) {
    const userId = getUserId();
    if (!userId) {
      return createApiResponse({
        success: false,
        message: 'User not authenticated'
      });
    }
    return this.addToWatchlist(userId, requestData);
  },

  async getCurrentUserWatchlistItems() {
    const userId = getUserId();
    if (!userId) {
      return createApiResponse({
        success: false,
        message: 'User not authenticated'
      });
    }
    return this.getWatchlistItems(userId);
  },

  async removeFromCurrentUserWatchlist(companyId) {
    const userId = getUserId();
    if (!userId) {
      return createApiResponse({
        success: false,
        message: 'User not authenticated'
      });
    }
    return this.removeFromWatchlist(userId, companyId);
  },

  async updateCurrentUserWatchlistItem(companyId, requestData) {
    const userId = getUserId();
    if (!userId) {
      return createApiResponse({
        success: false,
        message: 'User not authenticated'
      });
    }
    return this.updateWatchlistItem(userId, companyId, requestData);
  }
};
